import { useContext } from "react";
import { useTranslation } from "react-i18next";
import Modal from "src/components/Modal/Modal";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { SESSION_KEYS } from "src/consts/session";
import PageTitle from "src/components/Displays/PageTitle";
import BackButton from "src/components/Shared/BackButton";
import { Box, Link, Stack, useTheme } from "@mui/material";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";
import CopyOcppUrl from "src/components/MicroFrontend/Onboarding/CopyOcppUrl";
import { EvseSelfOnboardingDetails } from "src/models/smartChargingClientsModels";

const Intro = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const themeName = useContext(ThemeContext);

  const [isOverlayOpen, toggleIsOverlayOpen] = useToggle(false);

  const [getEvseSelfOnboardingData] =
    useSessionStorage<EvseSelfOnboardingDetails>(
      SESSION_KEYS.EVSE_SELF_ONBOARDING_DETAILS,
    );

  const evseSelfOnboardingData = getEvseSelfOnboardingData() ?? {
    uuid: "",
    connectionUrl: "",
    isAlreadyUsed: false,
    modelName: "",
    manufacturer: "",
    onboardingGuideUrlPath: "",
  };

  const handleNextClick = () => {
    navigate(SMART_CHARGING_ROUTES.CONNECT_CONFIRM);
  };

  const handleOnboardingSlider = () => {
    navigate(SMART_CHARGING_ROUTES.ONBOARDING_SLIDER);
  };

  return (
    <>
      <Stack gap={5}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <BackButton
            variant={
              themeName === ThemeValues.lichtblick ||
              themeName === ThemeValues.SMA
                ? "dark"
                : "light"
            }
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
              display: { xs: "none", sm: "flex" },
            })}
          />
          <Text
            text={t("page.wallboxConnect.pageTitle")}
            sx={(theme) => ({
              fontWeight: 500,
              fontSize: "32px",
              lineHeight: "40px",
              fontFamily: "officina",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>

        <Text
          text={t("page.wallboxConnect.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack
          gap={5}
          sx={{
            maxWidth: { sm: "380px" },
          }}
        >
          <Stack
            sx={{
              border: "1px solid lightgray",
              borderRadius: 2,
              px: 2,
              py: 4,
              gap: 3,
            }}
          >
            <Stack>
              <Text
                text={t("page.wallboxConnect.formText1")}
                sx={(theme) => ({
                  fontSize: "14px",
                  fontWeight: "bold",
                  lineHeight: "20px",
                  fontFamily: "Inter",
                  color: theme.palette.mainFont.primary,
                })}
              />
              <Text
                text={t("page.wallboxConnect.formText2")}
                sx={(theme) => ({
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  fontFamily: "Inter",
                  color: theme.palette.mainFont.primary,
                })}
              />

              <Text
                sx={(theme) => ({
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  fontFamily: "Inter",
                  color: theme.palette.mainFont.primary,
                })}
                text={evseSelfOnboardingData?.connectionUrl}
              />
            </Stack>

            <Box>
              <CopyOcppUrl
                id={evseSelfOnboardingData?.connectionUrl}
                message={`URL ${evseSelfOnboardingData?.connectionUrl} copied to clipboard`}
              />
            </Box>
          </Stack>

          <Stack gap={3}>
            {evseSelfOnboardingData &&
            evseSelfOnboardingData.manufacturer === "MENNEKES" &&
            evseSelfOnboardingData.modelName === "AMTRON 4You" ? (
              <Box>
                <PrimaryButton
                  sx={{
                    color: "#fff",
                    backgroundColor: "#000",
                    "&:hover": {
                      color: "#fff",
                      backgroundColor: "#000",
                    },
                  }}
                  onClick={handleOnboardingSlider}
                  text={t("page.wallboxConnect.helpWithSetup")}
                />
              </Box>
            ) : (
              <>
                {evseSelfOnboardingData.onboardingGuideUrlPath ? (
                  <Stack>
                    <Link
                      sx={(theme) => ({
                        color: "#fff",
                        backgroundColor: "#000",
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "22px",
                        borderRadius: "32px",
                        "&:hover": {
                          color: "#fff",
                          backgroundColor: "#000",
                          textDecoration: "none",
                        },
                        padding: "12px 24px 12px 24px",
                        textDecoration: "none",
                        width: { xs: "100%", sm: "fit-content" },
                        textAlign: "center",
                      })}
                      href={evseSelfOnboardingData.onboardingGuideUrlPath}
                      target="_blank"
                    >
                      {t("page.wallboxConnect.helpWithSetup")}
                    </Link>
                  </Stack>
                ) : (
                  <Box>
                    <PrimaryButton
                      sx={{
                        color: "#fff",
                        backgroundColor: "#000",
                        width: { xs: "100%", sm: "fit-content" },
                        "&:hover": {
                          color: "#fff",
                          backgroundColor: "#000",
                        },
                      }}
                      onClick={toggleIsOverlayOpen}
                      text={t("page.wallboxConnect.helpWithSetup")}
                    />
                  </Box>
                )}
              </>
            )}

            <Box>
              <PrimaryButton
                onClick={handleNextClick}
                text={t("page.wallboxConnect.nextButton")}
                sx={{ width: { xs: "100%", sm: "fit-content" } }}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.wallboxConnect.modal.title")}
          />
        }
        isOpen={isOverlayOpen}
        onClose={toggleIsOverlayOpen}
        onSubmit={toggleIsOverlayOpen}
        closeText={t("page.wallboxConnect.modal.closeText")}
      >
        <Stack gap={4}>
          <Stack>
            <Text
              text={t("page.wallboxConnect.modal.point1Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "bold",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnect.modal.point1Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnect.modal.point2Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "bold",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnect.modal.point2Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnect.modal.point3Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "bold",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnect.modal.point3Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

export default Intro;
